export interface CompanyValues {
  COMPANY_NAME?: string
  COMPANY_REGISTRATION_NUMBER?: string
  ADDRESS_WHOLE_ADDRESS?: string
  ADDRESS_CODE?: string
  [key: string]: string | undefined
}

export interface CompanySuggestion {
  values?: CompanyValues
  [key: string]: unknown
}

export interface CoverageValues {
  'povinne-limit'?: string
  'havarijni-rozsah'?: string
  'havarijni-uzemi'?: string
  'havarijni-spoluucast-percent'?: string
  'havarijni-spoluucast-fix'?: string
  'skla-rozsah'?: string
  'skla-limit'?: string
  'skla-spoluucast'?: string
  'urazove-rozsah'?: string
  'urazove-limit'?: string
  'zavazadla-limit'?: string
  'srazka-limit'?: string
  'nahradni-limit'?: string
  'strojni-limit'?: string
  gap?: boolean
  zivelni?: boolean
  asistencni?: boolean
  cinnost?: boolean
}

export const EMPTY_COVERAGE: CoverageValues = {
  'povinne-limit': '100/100 milionů Kč',
  'havarijni-rozsah': '-',
  'havarijni-uzemi': '-',
  'havarijni-spoluucast-percent': '-',
  'havarijni-spoluucast-fix': '-',
  'skla-rozsah': '-',
  'skla-limit': '-',
  'skla-spoluucast': '-',
  'urazove-rozsah': '-',
  'urazove-limit': '-',
  'zavazadla-limit': '',
  'srazka-limit': '',
  'nahradni-limit': '',
  'strojni-limit': '',
  gap: false,
  zivelni: false,
  asistencni: false,
  cinnost: false,
}

export interface GovData {
  Status?: number
  Data?: Record<string, unknown> & {
    CisloTp?: string
    CisloOrv?: string
    Specifikace?: string
    VozidloDruh2?: string
    VozidloDruh2R?: string
    DruhVozidla2?: string
    DruhVozidla2R?: string
  }
}

export interface Vehicle {
  id: number
  vin: string
  spz?: string
  najete_km?: string
  hodnota_vozidla?: string
  insurance_start_date?: string
  uziti_vozidla?: string
  owner_ico?: string
  operator_ico?: string
  owner_company?: CompanyValues | null
  operator_company?: CompanyValues | null
  ownerSameAsPolicyholder?: boolean
  operatorSameAsPolicyholder?: boolean
  // Set once the user edits this vehicle's owner/operator directly, so global
  // changes stop overwriting it.
  ownerOverridden?: boolean
  operatorOverridden?: boolean
  dalsi_informace?: string
  insurance?: CoverageValues | null
  car_gov_data?: GovData | null
  registry_not_found?: boolean
  specifikace?: string
  // Source document this vehicle was extracted from (for the "eye" preview).
  source_doc_id?: number | string
  // Control numbering "{document}.{vehicle}" (e.g. "1.1") for cross-checking
  // against the source document. Empty for manually added VINs.
  source?: string
}

export interface GeneralInfo {
  pojistnik: CompanyValues | Record<string, never>
  vat: boolean
  vlastnik: CompanyValues | Record<string, never>
  provozovatel: CompanyValues | Record<string, never>
  ownerSameAsPolicyholder: boolean
  operatorSameAsPolicyholder: boolean
  insurance_start_date: string
  uziti_vozidla: string
  dalsi_informace: string
  pojistne_kryti: CoverageValues
}

export interface PendingFile {
  id: string
  filename: string
  /** `warning` = doklad zpracován, ale bez použitelného VIN (např. přečtená jen SPZ). */
  status: 'uploading' | 'queued' | 'processed' | 'warning' | 'error'
  error?: string
}

export const POVINNE_LIMITY = [
  '50/50 milionů Kč',
  '50/60 milionů Kč',
  '62/62 milionů Kč',
  '70/70 milionů Kč',
  '100/100 milionů Kč',
  '150/150 milionů Kč',
  '200/200 milionů Kč',
  '300/300 milionů Kč',
  'Bez pojištění',
]

export const HAVARIJNI_ROZSAH = ['-', 'Allrisk', 'HAV + Odcizení', 'HAV + Živly', 'Bez pojištění']
export const HAVARIJNI_UZEMI = ['-', 'Evropa', 'Česká republika', 'Svět']
export const HAVARIJNI_SPOLUUCAST_PCT = ['-', '1% min. 1.000 Kč', '2% min. 2.000 Kč', '3% min. 3.000 Kč', '5% min. 5.000 Kč', '10% min. 10.000 Kč', '15% min. 15.000 Kč', '20% min. 20.000 Kč']
export const HAVARIJNI_SPOLUUCAST_FIX = ['-', '500 Kč', '1.000 Kč', '2.000 Kč', '5.000 Kč', '10.000 Kč', '20.000 Kč', '50.000 Kč']

export const SKLA_ROZSAH = ['-', 'Všechna skla', 'Čelní sklo', 'Bez pojištění']
export const SKLA_LIMIT = ['-', 'bez-limitu', '10000', '15000', '20000', '25000', '30000', '35000', '40000', '45000', '50000', '60000', '70000', '80000', '90000', '100000', '120000']
export const SKLA_SPOLUUCAST = ['-', 'Bez spoluúčasti', '500 Kč', '1.000 Kč', '10%']

export const URAZOVE_ROZSAH = ['-', 'Řidič', 'Celá posádka', 'Bez pojištění']
export const URAZOVE_LIMIT = ['-', '1x-limit', '2x-limit', '3x-limit', '4x-limit', '5x-limit']

export const UZITI_VOZIDLA = [
  'Běžné užití',
  'Autoškola',
  'Taxislužba',
  'Půjčovna vozidel',
  'Vozidla s právem přednosti',
  'ADR',
  'Záchranná služba',
  'Hasičský sbor',
  'Policie',
  'Vozidla ozbrojených složek',
  'Kurýrní služba',
  'Jiné',
]
